// frontend/src/pages/admin/Dashboard/Dashboard.jsx

import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

import { getAdminDashboardApi } from '../../../api/dashboard.api';
import Card from '../../../components/common/Card';
import Icon from '../../../components/common/Icon';
import { toJalaliMonthYear } from '../../../utils/jalali';
import { formatCurrency } from '../../../utils/constants';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* =========================================================
     GREETING
  ========================================================= */

  const greeting = useMemo(() => {
    const hour = new Date().getHours();

    if (hour < 12) return 'صبح بخیر';
    if (hour < 18) return 'ظهر بخیر';
    return 'شب بخیر';
  }, []);

  /* =========================================================
     LOAD DASHBOARD
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await getAdminDashboardApi();

        if (!mounted) return;

        setData(response?.data?.data || null);
      } catch (err) {
        if (!mounted) return;

        setError(
          err?.response?.data?.message ||
            'دریافت اطلاعات داشبورد با خطا مواجه شد'
        );

        setData(null);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return <DashboardSkeleton />;
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error) {
    return (
      <div className="space-y-6">
        <DashboardHeader greeting={greeting} />

        <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-red-100 text-red-500">
            <Icon name="alert-circle" className="h-7 w-7" />
          </div>

          <h2 className="mt-4 font-black text-red-700">
            دریافت اطلاعات ناموفق بود
          </h2>

          <p className="mt-2 text-sm text-red-500">
            {error}
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-red-700"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  /* =========================================================
     EMPTY
  ========================================================= */

  if (!data) {
    return (
      <div className="space-y-6">
        <DashboardHeader greeting={greeting} />

        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-100 text-slate-400">
            <Icon name="bar-chart" className="h-7 w-7" />
          </div>

          <h2 className="mt-4 font-black text-text-dark">
            اطلاعاتی برای نمایش وجود ندارد
          </h2>

          <p className="mt-2 text-sm text-slate-400">
            در حال حاضر داده‌ای برای داشبورد ثبت نشده است.
          </p>
        </div>
      </div>
    );
  }

  /* =========================================================
     SAFE DATA
  ========================================================= */

  const clubs = data.clubs || {};
  const players = data.players || {};
  const matches = data.matches || {};
  const tickets = data.tickets || {};
  const financials = data.financials || {};

  const totalIncome = Number(financials.totalIncome || 0);
  const totalExpense = Number(financials.totalExpense || 0);
  const balance = Number(financials.balance || 0);

  const balancePositive = balance >= 0;

  /* =========================================================
     CHART
  ========================================================= */

  const chartData = [
    {
      name: 'درآمد',
      مقدار: totalIncome,
      type: 'income',
    },
    {
      name: 'هزینه',
      مقدار: totalExpense,
      type: 'expense',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <DashboardHeader greeting={greeting} />

      {/* =====================================================
          MAIN STATISTICS
      ===================================================== */}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">

        <DashboardStatCard
          title="کل باشگاه‌ها"
          accent
          value={clubs.total}
          description={`${clubs.active || 0} فعال`}
          icon="club"
          iconClass="bg-emerald-50 text-emerald-600"
          valueClass="text-emerald-600"
        />

        <DashboardStatCard
          title="بازیکنان"
          value={players.total}
          description="بازیکن ثبت‌ شده"
          icon="users"
          iconClass="bg-blue-50 text-blue-600"
          valueClass="text-blue-600"
        />

        <DashboardStatCard
          title="مسابقات پیش رو"
          value={matches.upcoming}
          description={`${matches.thisMonth || 0} مسابقه این ماه`}
          icon="ball"
          iconClass="bg-amber-50 text-amber-600"
          valueClass="text-amber-600"
        />

        <DashboardStatCard
          title="تیکت‌ های باز"
          value={tickets.open}
          description="نیازمند بررسی"
          icon="ticket"
          iconClass="bg-rose-50 text-rose-600"
          valueClass="text-rose-600"
        />

      </div>

      {/* =====================================================
          QUICK OVERVIEW
      ===================================================== */}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

        <OverviewCard
          title="وضعیت باشگاه‌ها"
          icon="club"
          iconClass="bg-emerald-50 text-emerald-600"
        >
          <div className="space-y-4">

            <ProgressRow
              label="فعال"
              value={clubs.active || 0}
              total={clubs.total || 0}
              color="bg-emerald-500"
            />

            <ProgressRow
              label="در انتظار تأیید"
              value={clubs.pending || 0}
              total={clubs.total || 0}
              color="bg-amber-500"
            />

            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-xs text-slate-400">
                مجموع باشگاه‌ها
              </span>

              <span className="font-black text-slate-700">
                {formatNumber(clubs.total)}
              </span>
            </div>

          </div>
        </OverviewCard>

        <OverviewCard
          title="وضعیت مسابقات"
          icon="ball"
          iconClass="bg-amber-50 text-amber-600"
        >
          <div className="flex items-center justify-between">

            <div>
              <p className="text-xs font-bold text-slate-400">
                مسابقات پیش رو
              </p>

              <p className="mt-2 text-3xl font-black text-text-dark">
                {formatNumber(matches.upcoming)}
              </p>

              <p className="mt-1 text-xs text-slate-400">
                برنامه‌ریزی شده
              </p>
            </div>

            <div className="grid h-16 w-16 place-items-center rounded-2xl bg-amber-50 text-amber-600">
              <Icon name="calendar" className="h-7 w-7" />
            </div>

          </div>

          <div className="mt-5 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-xs font-bold text-slate-500">
              این ماه
            </span>

            <span className="text-sm font-black text-amber-600">
              {formatNumber(matches.thisMonth)} مسابقه
            </span>
          </div>
        </OverviewCard>

        <OverviewCard
          title="پشتیبانی"
          icon="ticket"
          iconClass="bg-rose-50 text-rose-600"
        >
          <div className="flex items-center justify-between">

            <div>
              <p className="text-xs font-bold text-slate-400">
                تیکت‌ های باز
              </p>

              <p className="mt-2 text-3xl font-black text-text-dark">
                {formatNumber(tickets.open)}
              </p>

              <p className="mt-1 text-xs text-slate-400">
                نیازمند پاسخ
              </p>
            </div>

            <div className="grid h-16 w-16 place-items-center rounded-2xl bg-rose-50 text-rose-600">
              <Icon name="message-circle" className="h-7 w-7" />
            </div>

          </div>

          <div className="mt-5 rounded-xl bg-rose-50 px-4 py-3 text-xs font-bold text-rose-600">
            بررسی تیکت‌ های جدید را فراموش نکنید.
          </div>
        </OverviewCard>

      </div>

      {/* =====================================================
          FINANCIAL + CHART
      ===================================================== */}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">

        {/* Financial Cards */}

        <div className="space-y-4 lg:col-span-4">

          <FinancialCard
            title="مجموع درآمد"
            value={totalIncome}
            icon="trendingUp"
            color="emerald"
          />

          <FinancialCard
            title="مجموع هزینه"
            value={totalExpense}
            icon="trendingDown"
            color="rose"
          />

          <FinancialCard
            title="تراز مالی"
            value={balance}
            icon={balancePositive ? 'trendingUp' : 'trendingDown'}
            color={balancePositive ? 'emerald' : 'rose'}
            highlighted
          />

        </div>

        {/* Chart */}

        <div className="lg:col-span-8">
          <Card className="h-full overflow-hidden">

            <div className="mb-5 flex items-center justify-between">

              <div>
                <h2 className="font-black text-text-dark">
                  وضعیت مالی
                </h2>

                <p className="mt-1 text-xs text-slate-400">
                  مقایسه درآمد و هزینه
                </p>
              </div>

              <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                <Icon name="bar-chart" className="h-5 w-5" />
              </div>

            </div>

            <div className="h-[280px] w-full">

              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 0,
                  }}
                  barCategoryGap="35%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#dfe2eb"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: '#64748b',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: '#94a3b8',
                      fontSize: 11,
                    }}
                    tickFormatter={(value) =>
                      compactNumber(value)
                    }
                  />

                  <Tooltip
                    cursor={{
                      fill: '#f8fafc',
                    }}
                    formatter={(value) => [
                      formatCurrency(value),
                      'مبلغ',
                    ]}
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '14px',
                      padding: '10px 14px',
                      boxShadow:
                        '0 10px 30px rgba(15, 23, 42, 0.08)',
                      direction: 'rtl',
                    }}
                    labelStyle={{
                      fontWeight: 800,
                      color: '#0f172a',
                      marginBottom: 5,
                    }}
                  />

                  <Bar
                    dataKey="مقدار"
                    radius={[10, 10, 0, 0]}
                    maxBarSize={70}
                    fill="#10b981"
                  />

                </BarChart>
              </ResponsiveContainer>

            </div>

          </Card>
        </div>

      </div>

      {/* =====================================================
          QUICK ACTIONS
      ===================================================== */}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

        <div className="mb-4">
          <h2 className="font-black text-text-dark">
            دسترسی سریع
          </h2>

          <p className="mt-1 text-xs text-slate-400">
            عملیات پرکاربرد مدیریت سامانه
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">

          <QuickAction
            title="بازیکنان"
            description="مدیریت بازیکنان"
            icon="users"
            className="bg-blue-50 text-blue-600"
            href="/admin/players"
          />

          <QuickAction
            title="باشگاه‌ها"
            description="مدیریت باشگاه‌ها"
            icon="club"
            className="bg-emerald-50 text-emerald-600"
            href="/admin/clubs"
          />

          <QuickAction
            title="مسابقات"
            description="برنامه مسابقات"
            icon="ball"
            className="bg-amber-50 text-amber-600"
            href="/admin/matches"
          />

          <QuickAction
            title="تیکت‌ها"
            description="پشتیبانی کاربران"
            icon="ticket"
            className="bg-rose-50 text-rose-600"
            href="/admin/tickets"
          />

        </div>

      </div>

      {/* =====================================================
          FOOTER INFO
      ===================================================== */}

      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Icon
            name="calendar"
            className="h-4 w-4 text-emerald-500"
          />

          <span>
            {toJalaliMonthYear(new Date())}
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs font-bold text-emerald-600">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          سامانه فعال است
        </div>

      </div>

    </div>
  );
}

/* =========================================================
   HEADER
========================================================= */

function DashboardHeader({ greeting }) {
  return (
    <div className="hero-panel relative isolate overflow-hidden rounded-[2rem] bg-ink p-6 text-white shadow-[0_30px_70px_-30px_rgba(7,21,15,0.7)] sm:p-8">

      <div className="absolute -left-10 -top-16 h-44 w-44 rounded-full bg-floodlight/20 blur-3xl" />
      <div className="absolute -bottom-20 right-10 h-52 w-52 rounded-full bg-emerald-400/25 blur-3xl" />

      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">

        <div>
          <div className="mb-3 flex items-center gap-2 text-floodlight">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-floodlight/15">
              <Icon name="dashboard" className="h-4 w-4" />
            </span>

            <span className="text-xs font-bold">
              داشبورد مدیریت
            </span>
          </div>

          <h1 className="text-2xl font-black sm:text-3xl">
            {greeting}، مدیر عزیز 👋
          </h1>

          <p className="mt-2 text-sm text-white/60">
            نمای کلی عملکرد و وضعیت سامانه
          </p>
        </div>

        <div className="glass-dark rounded-3xl px-5 py-3.5">
          <p className="text-[10px] font-bold text-white/55">
            تاریخ امروز
          </p>

          <p className="mt-1 text-sm font-black text-floodlight">
            {toJalaliMonthYear(new Date())}
          </p>
        </div>

      </div>
    </div>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

function DashboardStatCard({
  title,
  value,
  description,
  icon,
  iconClass,
  valueClass,
  accent = false,
}) {
  return (
    <div
      className={`group relative overflow-hidden rounded-[1.75rem] p-4 transition-all duration-500 hover:-translate-y-1.5 sm:p-5 ${
        accent ? 'accent-card' : 'glass hover:shadow-card-hover'
      }`}
    >

      <div className="flex items-start justify-between gap-3">

        <div className="min-w-0">

          <p className={`relative z-10 truncate text-xs font-bold sm:text-sm ${accent ? 'text-ink/70' : 'text-slate-400'}`}>
            {title}
          </p>

          <p
            className={`relative z-10 mt-2 text-2xl font-black sm:text-3xl ${accent ? 'text-ink' : valueClass}`}
          >
            {formatNumber(value)}
          </p>

          <p className={`relative z-10 mt-1 truncate text-[10px] sm:text-xs ${accent ? 'text-ink/60' : 'text-slate-400'}`}>
            {description}
          </p>

        </div>

        <div
          className={`relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-2xl transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110 sm:h-12 sm:w-12 ${accent ? 'bg-ink text-floodlight' : iconClass}`}
        >
          <Icon name={icon} className="h-5 w-5" />
        </div>

      </div>

    </div>
  );
}

/* =========================================================
   OVERVIEW CARD
========================================================= */

function OverviewCard({
  title,
  icon,
  iconClass,
  children,
}) {
  return (
    <Card>

      <div className="mb-5 flex items-center gap-3">

        <div
          className={`grid h-10 w-10 place-items-center rounded-xl ${iconClass}`}
        >
          <Icon name={icon} className="h-5 w-5" />
        </div>

        <h2 className="font-black text-text-dark">
          {title}
        </h2>

      </div>

      {children}

    </Card>
  );
}

/* =========================================================
   PROGRESS ROW
========================================================= */

function ProgressRow({
  label,
  value,
  total,
  color,
}) {
  const percentage =
    total > 0
      ? Math.min(100, Math.round((value / total) * 100))
      : 0;

  return (
    <div>

      <div className="mb-2 flex items-center justify-between">

        <span className="text-xs font-bold text-slate-500">
          {label}
        </span>

        <span className="text-xs font-black text-slate-700">
          {formatNumber(value)}
        </span>

      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all duration-700 ${color}`}
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>

      <p className="mt-1 text-[10px] text-slate-400">
        {percentage}٪
      </p>

    </div>
  );
}

/* =========================================================
   FINANCIAL CARD
========================================================= */

function FinancialCard({
  title,
  value,
  icon,
  color,
  highlighted = false,
}) {
  const isPositive = color === 'emerald';

  return (
    <Card
      className={`transition-all duration-300 hover:-translate-y-0.5 ${
        highlighted
          ? isPositive
            ? 'border-emerald-200 bg-emerald-50/30'
            : 'border-rose-200 bg-rose-50/30'
          : ''
      }`}
    >

      <div className="flex items-center justify-between gap-3">

        <div className="min-w-0">

          <p className="text-xs font-bold text-slate-400 sm:text-sm">
            {title}
          </p>

          <p
            className={`mt-2 truncate text-xl font-black sm:text-2xl ${
              isPositive
                ? 'text-emerald-600'
                : 'text-rose-600'
            }`}
          >
            {formatCurrency(value)}
          </p>

        </div>

        <div
          className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${
            isPositive
              ? 'bg-emerald-50 text-emerald-600'
              : 'bg-rose-50 text-rose-600'
          }`}
        >
          <Icon name={icon} className="h-5 w-5" />
        </div>

      </div>

    </Card>
  );
}

/* =========================================================
   QUICK ACTION
========================================================= */

function QuickAction({
  title,
  description,
  icon,
  className,
  href,
}) {
  return (
    <a
      href={href}
      className="group rounded-2xl border border-slate-200 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md sm:p-4"
    >

      <div
        className={`grid h-10 w-10 place-items-center rounded-xl transition-transform duration-300 group-hover:scale-110 ${className}`}
      >
        <Icon name={icon} className="h-5 w-5" />
      </div>

      <div className="mt-3">

        <p className="text-xs font-black text-slate-700 sm:text-sm">
          {title}
        </p>

        <p className="mt-1 truncate text-[10px] text-slate-400 sm:text-xs">
          {description}
        </p>

      </div>

    </a>
  );
}

/* =========================================================
   SKELETON
========================================================= */

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">

      {/* Header */}

      <div className="h-36 rounded-2xl bg-slate-200" />

      {/* Stats */}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">

        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="rounded-2xl border border-slate-200 bg-white p-5"
          >
            <div className="flex items-start justify-between">

              <div className="space-y-3">
                <div className="h-3 w-20 rounded bg-slate-200" />
                <div className="h-8 w-24 rounded bg-slate-200" />
                <div className="h-2.5 w-28 rounded bg-slate-100" />
              </div>

              <div className="h-11 w-11 rounded-xl bg-slate-200" />

            </div>
          </div>
        ))}

      </div>

      {/* Overview */}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="rounded-2xl border border-slate-200 bg-white p-5"
          >
            <div className="mb-6 h-6 w-32 rounded bg-slate-200" />

            <div className="space-y-5">
              <div className="h-4 rounded bg-slate-100" />
              <div className="h-4 rounded bg-slate-100" />
              <div className="h-4 rounded bg-slate-100" />
            </div>
          </div>
        ))}

      </div>

      {/* Financial */}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">

        <div className="space-y-4 lg:col-span-4">

          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-24 rounded-2xl bg-white shadow-sm"
            />
          ))}

        </div>

        <div className="h-[360px] rounded-2xl bg-white shadow-sm lg:col-span-8" />

      </div>

    </div>
  );
}

/* =========================================================
   HELPERS
========================================================= */

function formatNumber(value) {
  return Number(value || 0).toLocaleString('fa-IR');
}

function compactNumber(value) {
  const number = Number(value || 0);

  if (number >= 1_000_000_000) {
    return `${Math.round(number / 1_000_000_000)}B`;
  }

  if (number >= 1_000_000) {
    return `${Math.round(number / 1_000_000)}M`;
  }

  if (number >= 1_000) {
    return `${Math.round(number / 1_000)}K`;
  }

  return number.toLocaleString('fa-IR');
}